/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import React from 'react';
import Props, {DispatchProps, StateProps} from './Props';
import {RootState} from '../../reducers';
import {Dispatch} from 'redux';
import {connect} from 'react-redux';
import {
	loginSceneAccountNameInputChanged,
	loginSceneActivated,
	loginSceneCredentialsSubmitted,
	loginSceneDeactivated,
	loginSceneNewPasswordInputChanged,
	loginSceneNewPasswordSubmitted,
	loginScenePasswordInputChanged,
	loginScenePhoneVerificationCodeInputChanged,
	loginScenePhoneVerificationCodeSubmitted,
	loginSceneRememberMeTokenSubmitted,
	loginSceneRequestRestartPhoneVerification,
	loginSceneRequestRetryPhoneVerification,
	loginSceneResetButtonClicked
} from './actions';
import {DisplayType} from './displayType';
import CenteredFormPanel from '../../components/CenteredFormPanel';
import EnterPassword from '../../components/EnterPassword';
import Credentials from './components/Credentials';
import RememberMe from './components/RememberMe';
import PhoneVerificationCode from '../../components/PhoneVerificationCode';
import RetryPhoneVerificationStart from '../../components/RetryPhoneVerificationStart';
import {TokenType} from '../../services/LocalStorage/TokenType';
import {localStorage} from '../../services';

class Login extends React.Component<Props> {

	componentDidMount() {
		const props: Readonly<Props> = this.props;

		props.onActivate();
	}

	componentWillUnmount() {
		const props: Readonly<Props> = this.props;

		props.onDeactivate();
	}

	render() {
		const props: Readonly<Props> = this.props;

		let withCenteredFormPanel = true;
		let display: JSX.Element | null;

		switch (props.index.displayType) {
			case DisplayType.DISPLAY_TYPE_REMEMBER_ME:
				withCenteredFormPanel = false;
				display = <RememberMe onSubmitRememberMeToken={props.rememberMeDispatch.onSubmitRememberMeToken}/>;
				break;
			case DisplayType.DISPLAY_TYPE_CREDENTIALS:
				display = <Credentials
					submitDisabled={props.credentials.submitDisabled}
					violations={props.credentials.violations}
					onAccountNameChange={props.credentialsDispatch.onAccountNameChange}
					onPasswordChange={props.credentialsDispatch.onPasswordChange}
					onSubmitCredentials={props.credentialsDispatch.onSubmitCredentials}
					onResetPasswordClick={props.credentialsDispatch.onResetPasswordClick}/>;
				break;
			case DisplayType.DISPLAY_TYPE_RETRY_PHONE_VERIFICATION_START:
				display = <RetryPhoneVerificationStart
					submitDisabled={props.retryPhoneVerificationStart.submitDisabled}
					buttonText={props.retryPhoneVerificationStart.buttonText}
					onRetryPhoneVerificationStart={props.retryPhoneVerificationStartDispatch.onRetryPhoneVerificationStart}/>;
				break;
			case DisplayType.DISPLAY_TYPE_VERIFICATION_CODE:
				display = <PhoneVerificationCode
					verificationStopped={props.verificationCode.verificationStopped}
					submitDisabled={props.verificationCode.submitDisabled}
					violations={props.verificationCode.violations.verificationCode}
					maximumDurationInSeconds={props.verificationCode.maximumDurationInSeconds}
					onSubmitVerificationCode={props.verificationCodeDispatch.onSubmitVerificationCode}
					onVerificationCodeChange={props.verificationCodeDispatch.onVerificationCodeChange}
					onSubmitVerificationRestart={props.verificationCodeDispatch.onSubmitVerificationRestart}
					onRetryPhoneVerificationStart={props.verificationCodeDispatch.onRetryPhoneVerificationStart}/>;
				break;
			case DisplayType.DISPLAY_TYPE_ENTER_PASSWORD:
				display = <EnterPassword
					strength={props.enterPassword.strength}
					minimumStrength={props.enterPassword.minimumStrength}
					submitDisabled={props.enterPassword.submitDisabled}
					email={props.enterPassword.email}
					violations={props.enterPassword.violations.password}
					onPasswordChange={props.enterPasswordDispatch.onPasswordChange}
					onSubmitPassword={props.enterPasswordDispatch.onSubmitPassword}/>;
				break;
			default:
				display = null;
		}

		return (
			withCenteredFormPanel ?
				<CenteredFormPanel title="Inloggen" message={props.index.message} messageType={props.index.messageType}>
					{display}
				</CenteredFormPanel> :
				<div>
					{display}
				</div>
		);
	}
}

const mapStateToProps = (state: RootState): StateProps => ({
	credentials: state.login.credentials,
	enterPassword: state.login.enterPassword,
	index: state.login.index,
	retryPhoneVerificationStart: state.login.retryPhoneVerificationStart,
	verificationCode: state.login.verificationCode
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
	credentialsDispatch: {
		onAccountNameChange: () => dispatch(loginSceneAccountNameInputChanged()),
		onPasswordChange: () => dispatch(loginScenePasswordInputChanged()),
		onResetPasswordClick: () => dispatch(loginSceneResetButtonClicked()),
		onSubmitCredentials: (accountName, password, rememberMe) =>
			dispatch(loginSceneCredentialsSubmitted({
				command: {
					accountName,
					clientToken: localStorage.getToken(TokenType.CLIENT, accountName),
					password,
					rememberMe,
					secondFactorToken: localStorage.getToken(TokenType.SECOND_FACTOR, accountName)
				}
			}))
	},
	enterPasswordDispatch: {
		onPasswordChange: (newPassword) => dispatch(loginSceneNewPasswordInputChanged({password: newPassword})),
		onSubmitPassword: (password) => dispatch(loginSceneNewPasswordSubmitted({command: {password}}))
	},
	onActivate: () => dispatch(loginSceneActivated()),
	onDeactivate: () => dispatch(loginSceneDeactivated()),
	rememberMeDispatch: {
		onSubmitRememberMeToken: () => dispatch(loginSceneRememberMeTokenSubmitted())
	},
	retryPhoneVerificationStartDispatch: {
		onRetryPhoneVerificationStart: () => dispatch(loginSceneRequestRetryPhoneVerification())
	},
	verificationCodeDispatch: {
		onRetryPhoneVerificationStart: () => dispatch(loginSceneRequestRetryPhoneVerification()),
		onSubmitVerificationCode: (verificationCode) => dispatch(loginScenePhoneVerificationCodeSubmitted({command: {verificationCode}})),
		onSubmitVerificationRestart: () => dispatch(loginSceneRequestRestartPhoneVerification()),
		onVerificationCodeChange: () => dispatch(loginScenePhoneVerificationCodeInputChanged())
	}
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
